import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { openTemplateEmail } from '../email-templates/mailto.js';
import { downloadTemplateEml } from '../email-templates/eml.js';
import {
  PROOF_OF_PAYMENT,
  REMOVE_MERCHANT_BLOCK,
  MISSING_PAYMENTS,
  SEPA_COMPLIANCE,
} from '../email-templates/templates.js';
import { useContentMap } from '../content.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { formatOwners } from '../lib/owners.js';

/**
 * SCRN 46 — Transactions (CCF-47 Historic listing) and SCRN 48 — Pending
 * Transactions (CCF-32), combined into one screen as in the Figma frame: the
 * agent picks an account, then a transaction option (Historic or Pending).
 *
 * Faithful port of the customer's Figma "Transactions homescreen" design. The
 * shared agent banner ("NavDark") lives in the app shell (AppLayout), so this
 * screen renders only the `<main>` content. Hardcoded sample data is replaced
 * with live data from the Teller-ID-gated BFF via the generated typed client:
 *   - accounts:  POST /api/customers/accounts
 *   - historic:  POST /api/accounts/transactions
 *   - pending:   POST /api/accounts/transactions/pending
 */

/** A single account row (open contract shape; subset the selector renders). */
interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  Balance?: number;
  AvailableBalance?: number;
  Currency?: string;
  /**
   * Account-holder name(s) (CCF-80). The BFF adapter (`deriveOwners`) joins
   * joint holders with an ampersand ("A & B"); a single-holder account carries
   * one name. Drives the "Account owner" summary — for a joint account ALL
   * owners are listed (issue #382).
   */
  Owners?: string;
}

/** A transaction row (open contract shape; the rendered subset). */
interface TxnRow {
  TransactionId: string;
  /** 6-digit transaction reference shown in the Ref column (CCF-47). */
  Ref?: string;
  Date: string;
  Description: string;
  Amount: number;
  DrCr: 'DR' | 'CR';
  RunningBalance?: number;
  Status?: string;
  Type?: string;
  Merchant?: string;
  /** Pending-hold auto-expiry date (CCF-32; PENDING only). */
  ExpiryDate?: string;
  /** Pending-hold type M/P/A/C (CCF-32; PENDING only). */
  HoldType?: string;
  // Composite key the real gateway needs to resolve the drill-down detail
  // (`transaction/information`) — threaded into the detail call (issue #125).
  InputDate?: string;
  InputTime?: string;
  InputTime2?: string;
  SequenceNumber?: string;
  /** Non-empty for SEPA transactions; threaded to the drill-down (#125). */
  SepaKeys?: string;
}

/** CCF-32 per-hold-type bucket totals shown in the Pending summary overview. */
interface PendingSummary {
  PosHolds?: number;
  VppHolds?: number;
  AtmHolds?: number;
  ChqHolds?: number;
  StmHolds?: number;
  PrtHolds?: number;
}

interface PendingResult {
  AccountNumber?: string;
  PendingTransactions?: TxnRow[];
  PendingCount?: number;
  TotalPending?: number;
  Summary?: PendingSummary;
}

/**
 * Snapshot of the transactions search state, persisted so it can be restored
 * when the agent drills into a transaction and then returns via the dedicated
 * "Back to transaction listing" link (issue #55). Entering the screen by any
 * other route (menu, fresh navigation, process menu, account dashboard link)
 * does NOT carry the restore flag, so the snapshot is discarded and the screen
 * starts clean.
 */
/**
 * Snapshot of the transactions *search criteria* (issue #55). Deliberately
 * holds only the query inputs — NOT the result rows. On restore the screen
 * re-runs the gated BFF call with these criteria, so the server-side guards
 * (auth/session, Teller-ID, RBAC permission, customer verification, and
 * account-ownership) are re-enforced and an expired/cleared session fails
 * closed (401/403) rather than the screen surfacing stale cached banking data.
 */
interface TransactionsSnapshot {
  /**
   * The verified customer (CIF) the snapshot was captured for. On restore we
   * require this to match the *current* verified customer before applying it —
   * otherwise a stale snapshot left in the tab (e.g. after the context changes
   * to a new customer) could re-issue a query under another customer's context.
   * Mismatches are discarded and the screen starts clean.
   */
  customerNumber: string;
  selectedAccount: string;
  option: string;
  fromDate: string;
  toDate: string;
  selectedType: string;
  amountSearch: string;
  /**
   * Set when this listing was reached FROM a Security screen's "Check
   * Transactions" action (issue #344) — carried through a transaction-detail
   * drill-down/back round trip so the "Back to High Security" button survives
   * it (otherwise the agent would lose their way back after viewing one
   * transaction's detail).
   */
  fromSecurity?: boolean;
  /** The Security screen route to return to (paired with `fromSecurity`). */
  returnTo?: string;
}

/**
 * sessionStorage key for the last transactions query snapshot. Scoped to the
 * tab/interaction (same store the customer context uses), so it is naturally
 * cleared when the agent clears the session or closes the tab.
 */
const SNAPSHOT_KEY = 'ptsb.transactionsQuery';

/**
 * Loads the last query snapshot, but only when it belongs to the currently
 * verified customer (`forCustomer`). A snapshot for any other customer — or a
 * snapshot with no/empty customer when one is in context — is treated as stale
 * and ignored, so cross-customer banking data can never be restored.
 */
function loadSnapshot(forCustomer: string): TransactionsSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as TransactionsSnapshot;
    if (!forCustomer || snap.customerNumber !== forCustomer) return null;
    return snap;
  } catch {
    return null;
  }
}

function saveSnapshot(snapshot: TransactionsSnapshot): void {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // Non-fatal: storage may be unavailable (private mode); the back-restore is
    // a convenience and the screen still works without it.
  }
}

function clearSnapshot(): void {
  try {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    // Non-fatal.
  }
}

/**
 * CCF-47 transaction-type filter options (Unibanks type codes + label). The
 * agent picks one of: D-Deposits, F-Fees/OD Interest, I-Credit Interest,
 * P-Standing Orders, W-Withdrawals, B-Direct Debit Transactions (or All).
 */
const TRANSACTION_TYPES = [
  { value: '', label: 'All transaction types' },
  { value: 'D', label: 'D - Deposits' },
  { value: 'F', label: 'F - Fees / OD Interest' },
  { value: 'I', label: 'I - Credit Interest' },
  { value: 'P', label: 'P - Standing Orders' },
  { value: 'W', label: 'W - Withdrawals' },
  { value: 'B', label: 'B - Direct Debit Transactions' },
] as const;

/** CCF-32 pending-hold-type code → human label (the "CHQ/MGT" column). */
const HOLD_TYPE_LABEL: Record<string, string> = {
  M: 'M - Management',
  P: 'P - POS (Visa debit)',
  A: 'A - ATM',
  C: 'C - Cheque',
};

/**
 * Maximum pending-hold rows shown per page in the read-only overview (#481 —
 * "List lengths"; the Pending list is capped and paged with Previous/Next).
 * Pagination is client-side over the already-loaded list.
 */
const PENDING_TXN_PER_PAGE = 6;

/**
 * Maximum historic-transaction rows shown per page (#481 — "List lengths").
 * Client-side pagination over the loaded listing; drill-down is preserved on
 * the paged rows.
 */
const HISTORIC_TXN_PER_PAGE = 16;

function formatCurrency(amount?: number, currency = 'EUR'): string {
  if (typeof amount !== 'number') return '';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** Formats an ISO date (YYYY-MM-DD) as DD/MM/YYYY for display. */
function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Converts a DD/MM/YYYY input to the API's YYYY-MM-DD, or '' if incomplete. */
function toIsoDate(ddmmyyyy: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(ddmmyyyy.trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

/**
 * Builds the inline error message for a failed BFF call (issue #81 §6.5). A
 * 403 is a permissions hint; otherwise, when the BFF surfaces the Bank gateway's
 * own `gateway: { code, message }` (e.g. `Code 1445` missing-field), show it so
 * the agent sees the actual reason rather than a bare "search failed 400".
 */
function gatewayErrorMessage(
  apiError: unknown,
  status: number,
  fallback: string,
): string {
  if (status === 403) return 'Forbidden: check your Teller ID and permissions.';
  const gw = (apiError as { gateway?: { code?: unknown; message?: unknown } })
    ?.gateway;
  if (gw && (gw.code !== undefined || gw.message)) {
    const code = gw.code !== undefined ? `${gw.code}` : '';
    const message = typeof gw.message === 'string' ? gw.message : '';
    const detail = [code && `Code ${code}`, message].filter(Boolean).join(': ');
    if (detail) return `${fallback} — ${detail}`;
  }
  return `${fallback} (${status}).`;
}

/**
 * Masks free-text date entry to DD/MM/YYYY as the agent types: any non-digit is
 * dropped and the `/` separators are auto-inserted, so typing `01022022`
 * produces `01/02/2022`. Digits beyond eight are ignored. This is purely a
 * display/entry helper — `validateDate` still checks the resulting value.
 */
function formatDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  let out = dd;
  if (digits.length > 2) out += `/${mm}`;
  if (digits.length > 4) out += `/${yyyy}`;
  return out;
}

/** Parses a masked DD/MM/YYYY string into a Date, or null if not a real date. */
function parseDmy(value: string): Date | null {
  const parts = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!parts) return null;
  const day = Number(parts[1]);
  const month = Number(parts[2]);
  const year = Number(parts[3]);
  const d = new Date(year, month - 1, day);
  if (
    d.getDate() !== day ||
    d.getMonth() !== month - 1 ||
    d.getFullYear() !== year
  ) {
    return null;
  }
  return d;
}

/**
 * Validates a single DD/MM/YYYY date is a real date and not in the future.
 * The 12-month limit is a restriction on the search *span* (From→To), not on
 * how far back a single date may reach — see {@link validateRange} (CCF-47:
 * "12-month restriction per search"). A search may cover any 12-month window.
 */
function validateDate(value: string): string {
  if (!value) return '';
  // The input is masked to DD/MM/YYYY, so a value that doesn't match the full
  // pattern is an incomplete entry — flag it rather than silently accepting it.
  if (!/^(\d{2})\/(\d{2})\/(\d{4})$/.test(value)) {
    return 'Please enter a valid date in DD/MM/YYYY format';
  }
  const d = parseDmy(value);
  if (!d) return 'Please enter a valid date';
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  if (d > today) {
    return 'Date cannot be in the future';
  }
  return '';
}

/**
 * Live (while-typing) variant of {@link validateDate} (issue #365 — "manual
 * entry generates errors"). While the agent is still mid-typing an incomplete
 * date we do NOT flag the "please enter DD/MM/YYYY" format error — a spurious
 * error on every keystroke of a perfectly good date is exactly the friction the
 * issue reports. Once the field holds a *complete* 10-char DD/MM/YYYY entry we
 * defer to the full {@link validateDate} so a genuinely invalid (e.g. 32/13) or
 * future date is still caught immediately. The full check also runs on blur (so
 * an entry left incomplete is still flagged when the agent leaves the field).
 */
function validateDateWhileTyping(value: string): string {
  if (!value) return '';
  // Incomplete entry: don't nag mid-typing. `formatDateInput` caps the value at
  // DD/MM/YYYY (10 chars), so anything shorter is still being typed.
  if (!/^(\d{2})\/(\d{2})\/(\d{4})$/.test(value)) return '';
  return validateDate(value);
}

/**
 * Validates the From→To search range (CCF-47): the From date must not be after
 * the To date, and a single search may span at most a 12-month period over any
 * window. Returns the message to display, or '' when the range is acceptable.
 * No-op until both dates are real (per-field errors are surfaced separately).
 */
function validateRange(from: string, to: string): string {
  const f = parseDmy(from);
  const t = parseDmy(to);
  if (!f || !t) return '';
  if (t < f) return 'To date must be on or after the From date';
  const maxTo = new Date(f);
  maxTo.setMonth(maxTo.getMonth() + 12);
  if (t > maxTo) {
    return 'Incorrect timeframe — please ensure the dates are within a 12-month period';
  }
  return '';
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'all'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Formats a Date back to the masked DD/MM/YYYY string the inputs expect. */
function dateToDmy(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** True when two Dates fall on the same calendar day. */
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * A self-contained calendar date-picker popover. A calendar-icon trigger sits
 * next to the existing masked text input (which keeps handling manual typing);
 * picking a day calls {@link onSelect} with a masked DD/MM/YYYY string so the
 * date flows through the *same* state setters + `validateDate`/`validateRange`
 * as manual entry. Future dates are not selectable (max = today). Mirrors the
 * {@link Dropdown} open/close + click-outside + keyboard pattern.
 */
function DatePicker({
  fieldLabel,
  value,
  onSelect,
}: {
  /** Human label of the field this picker drives (e.g. "From"), for a11y. */
  fieldLabel: string;
  /** Current masked DD/MM/YYYY value of the field, used to seed the view. */
  value: string;
  onSelect: (ddmmyyyy: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Today, normalised to midnight, is the latest selectable day.
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // The selected day (if the current value is a real date), else null.
  const selected = parseDmy(value);

  // The month currently shown: the selected value's month if valid, else the
  // current month. Re-seeded each time the popover opens.
  const [viewYear, setViewYear] = useState((selected ?? today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected ?? today).getMonth());

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onClickOutside);
        document.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [open]);

  function toggle(): void {
    setOpen((o) => {
      const next = !o;
      // Re-seed the view to the entered value (or today) each time we open.
      if (next) {
        const seed = parseDmy(value) ?? today;
        setViewYear(seed.getFullYear());
        setViewMonth(seed.getMonth());
      }
      return next;
    });
  }

  function changeMonth(delta: number): void {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }

  // Jump the view to a chosen month/year (issue #365 — direct month/year
  // selection, so an old date like 2019 is one click away rather than needing
  // dozens of "previous month" presses). Clamp to today so the view can never
  // land wholly in the future: if the chosen month/year is after the current
  // month, snap back to the current month (future days are non-selectable
  // anyway, but this keeps the grid useful and the next-month nav honest).
  function jumpTo(month: number, year: number): void {
    const chosenStart = new Date(year, month, 1);
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    if (chosenStart > thisMonthStart) {
      setViewYear(today.getFullYear());
      setViewMonth(today.getMonth());
      return;
    }
    setViewYear(year);
    setViewMonth(month);
  }

  // Years offered in the dropdown: this year back 30 years (well past the 2019
  // example in #365), newest first so recent years are at the top.
  const currentYear = today.getFullYear();
  const yearOptions: number[] = [];
  for (let y = currentYear; y >= currentYear - 30; y--) yearOptions.push(y);

  function pick(day: number): void {
    const d = new Date(viewYear, viewMonth, day);
    if (d > today) return; // future dates are not selectable
    onSelect(dateToDmy(d));
    setOpen(false);
  }

  // Build the month grid. JS getDay(): 0=Sun..6=Sat; we render Monday-first.
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  // Disable the "next month" nav once it would move entirely past today.
  const nextMonthStart = new Date(viewYear, viewMonth + 1, 1);
  const nextDisabled = nextMonthStart > today;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Open calendar for the ${fieldLabel} field`}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#1d252c] hover:text-[#005fcc] focus:outline-none focus:ring-2 focus:ring-[#005fcc]/40 rounded"
      >
        <svg
          className="size-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={`${fieldLabel} field calendar`}
          className="absolute right-0 top-full mt-2 w-[280px] bg-white border-2 border-[#d1d5db] rounded-md shadow-lg z-[100] p-3 font-['Arial',sans-serif]"
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              aria-label="Previous month"
              className="p-1 text-[#1d252c] hover:text-[#005fcc] focus:outline-none focus:ring-2 focus:ring-[#005fcc]/40 rounded"
            >
              <svg
                className="size-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <label
                htmlFor={`${fieldLabel}-cal-month`}
                className="sr-only"
              >{`${fieldLabel} field — month`}</label>
              <select
                id={`${fieldLabel}-cal-month`}
                aria-label={`${fieldLabel} field — month`}
                value={viewMonth}
                onChange={(e) => jumpTo(Number(e.target.value), viewYear)}
                className="px-2 py-1 border-2 border-[#d1d5db] bg-white rounded-md text-[14px] font-bold text-[#1d252c] cursor-pointer focus:outline-none focus:border-[#005fcc]"
              >
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i}>
                    {name}
                  </option>
                ))}
              </select>
              <label
                htmlFor={`${fieldLabel}-cal-year`}
                className="sr-only"
              >{`${fieldLabel} field — year`}</label>
              <select
                id={`${fieldLabel}-cal-year`}
                aria-label={`${fieldLabel} field — year`}
                value={viewYear}
                onChange={(e) => jumpTo(viewMonth, Number(e.target.value))}
                className="px-2 py-1 border-2 border-[#d1d5db] bg-white rounded-md text-[14px] font-bold text-[#1d252c] cursor-pointer focus:outline-none focus:border-[#005fcc]"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => changeMonth(1)}
              disabled={nextDisabled}
              aria-label="Next month"
              className="p-1 text-[#1d252c] hover:text-[#005fcc] focus:outline-none focus:ring-2 focus:ring-[#005fcc]/40 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-[#1d252c]"
            >
              <svg
                className="size-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
          <div
            role="grid"
            aria-label={`${fieldLabel} field — choose a day`}
            className="grid grid-cols-7 gap-1"
          >
            {WEEKDAY_LABELS.map((w) => (
              <div
                key={w}
                role="columnheader"
                className="text-center text-[12px] font-bold text-[#6b7280] py-1"
              >
                {w}
              </div>
            ))}
            {cells.map((day, i) => {
              if (day === null)
                return <div key={`blank-${i}`} aria-hidden="true" />;
              const cellDate = new Date(viewYear, viewMonth, day);
              const isFuture = cellDate > today;
              const isSelected = selected ? sameDay(cellDate, selected) : false;
              const isToday = sameDay(cellDate, today);
              return (
                <button
                  key={day}
                  type="button"
                  role="gridcell"
                  disabled={isFuture}
                  aria-disabled={isFuture}
                  aria-pressed={isSelected}
                  aria-label={dateToDmy(cellDate)}
                  onClick={() => pick(day)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      pick(day);
                    }
                  }}
                  className={`text-center text-[14px] rounded py-1.5 focus:outline-none focus:ring-2 focus:ring-[#005fcc]/40 ${
                    isFuture
                      ? 'text-[#cbd5e1] cursor-not-allowed'
                      : 'text-[#1d252c] hover:bg-[#f3f4f6] cursor-pointer'
                  } ${isSelected ? 'bg-[#FF703E] text-[#1d252c] font-bold hover:bg-[#e55a2a]' : ''} ${
                    isToday && !isSelected ? 'border border-[#005fcc]' : ''
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Email-templates options, faithful to the Figma "Transactions homescreen"
 * frame.
 *
 * - "Add/remove merchant block" pops the **Request to Remove Merchant Block**
 *   template (#254, Drive "SCRN 46 & 47") as a prefilled `mailto:` draft (to
 *   the Card Operations Disputes mailbox) for the agent to send from Outlook,
 *   reusing the shared #254 mechanism ({@link openTemplateEmail}).
 * - "Proof of payment" pops the **Proof of Payment** template as a prefilled
 *   `mailto:` draft (to the SEPA queries mailbox). Originally (#254) this was a
 *   stub "coming soon" toast because the listing lacks the per-transaction
 *   detail the drill-down screen (SCRN-47) carries; @dylan219 (#738) overrode
 *   that — the option must pop the real draft now, prefilled best-effort from
 *   the listing context (customer name + selected account number). The
 *   per-transaction fields the listing doesn't have (destination name, posting
 *   date, reference, amount) stay blank for the agent to complete in the draft,
 *   matching the #254 blank-field-for-unknowns convention. No PAN is ever
 *   prefilled (PCI).
 */
const EMAIL_TEMPLATES = [
  'Proof of payment',
  'Add/remove merchant block',
  // #771 (@dylan219) — the two new payment-screen templates are offered on the
  // Transactions listing (SCRN-46) too. Both provide an HTML body, so they are
  // delivered as WAF-safe downloadable `.eml` drafts (gzip+base64 transport).
  'Missing payments',
  'SEPA compliance',
];

/**
 * Email-templates menu button (Figma): an orange button in the header row that
 * opens a menu of the named templates. Shown on the Historic-transactions AND
 * the Pending Transactions (SCRN-48) views (#795). Mirrors the {@link Dropdown}
 * open/close + click-outside + keyboard pattern (menu semantics, since the
 * options are actions not values).
 */
function EmailTemplatesMenu({
  customerName,
  accountNumber,
  content,
  tellerId,
  opener,
}: {
  customerName: string;
  accountNumber: string;
  /**
   * #637 inc 17 — the resolved content-catalogue map, threaded into the Remove
   * Merchant Block email template so its editable wording (subject, heading, SLA
   * line, field labels and the Team-Leader note) reflects any admin reword;
   * absent-key defaults keep the output byte-identical.
   */
  content?: Readonly<Record<string, string>>;
  /**
   * #771 — the agent's Teller ID, forwarded to the WAF-safe `.eml` download for
   * the two HTML-bodied templates (Missing Payments / SEPA Compliance).
   */
  tellerId?: string;
  /** Injectable opener (tests); defaults to `window.location.assign`. */
  opener?: (url: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onClickOutside);
        document.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Email templates"
        className="inline-flex items-center justify-center w-[260px] px-6 py-3 min-h-[44px] bg-[#FF703E] text-[#1d252c] font-['Arial',sans-serif] text-[16px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] transition-all focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20"
      >
        <svg
          className="size-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m2 7 10 6 10-6" />
        </svg>
        <span className="ml-2 mr-5">Email templates</span>
        <svg
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            d="M2 4L6 8L10 4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Email template options"
          className="absolute right-0 mt-2 w-[260px] bg-white border-2 border-[#dad7d3] rounded-[4px] shadow-lg z-[100] overflow-hidden"
        >
          {EMAIL_TEMPLATES.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                if (option === 'Add/remove merchant block') {
                  // #254: pop the Request to Remove Merchant Block template as
                  // a prefilled mailto: draft (customer name + selected account
                  // number). The card number is deliberately NOT prefilled — a
                  // full PAN must never be rendered in an email body (PCI).
                  openTemplateEmail(
                    REMOVE_MERCHANT_BLOCK,
                    { customerName, accountNumber, content },
                    opener,
                  );
                  return;
                }
                if (option === 'Missing payments') {
                  // #771 — Missing Payments has an HTML body, so it downloads as
                  // a formatted, WAF-safe `.eml` draft (gzip+base64). Prefilled
                  // best-effort from the in-context customer name (→ Account
                  // Name) + selected account number; the per-transaction fields
                  // stay blank for the agent (no PAN prefilled — PCI).
                  downloadTemplateEml(
                    MISSING_PAYMENTS,
                    { customerName, accountNumber, content },
                    undefined,
                    tellerId,
                  );
                  return;
                }
                if (option === 'SEPA compliance') {
                  // #771 — SEPA Compliance has an HTML body, so it downloads as a
                  // formatted, WAF-safe `.eml` draft (gzip+base64). Its source
                  // fields are per-transaction and left blank for the agent.
                  downloadTemplateEml(
                    SEPA_COMPLIANCE,
                    { customerName, accountNumber, content },
                    undefined,
                    tellerId,
                  );
                  return;
                }
                // #738 (@dylan219) — "Proof of payment" pops the real Proof of
                // Payment draft from the listing (the #254-era stub toast is
                // removed). Prefilled from the in-context customer name +
                // selected account number + content catalogue; the
                // per-transaction fields the listing doesn't carry (destination
                // name, posting date, reference, amount) stay blank for the
                // agent to fill in the draft (#254 blank-field convention), and
                // no PAN is prefilled (PCI).
                openTemplateEmail(
                  PROOF_OF_PAYMENT,
                  { customerName, accountNumber, content },
                  opener,
                );
              }}
              className="w-full text-left px-5 py-3.5 min-h-[44px] text-[16px] font-['Arial',sans-serif] text-[#1d252c] hover:bg-[#F8EEE3] transition-colors border-b border-[#e8e5e1] last:border-b-0 focus:outline-none focus:bg-[#F8EEE3] whitespace-nowrap"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function TransactionsScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  // #637 inc 17 — the resolved content-catalogue map, threaded into the Remove
  // Merchant Block email template (via EmailTemplatesMenu) so its editable
  // wording reflects any admin reword; absent-key defaults keep it unchanged.
  const contentMap = useContentMap();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  const locationState = location.state as {
    restore?: boolean;
    fromSecurity?: boolean;
    returnTo?: string;
  } | null;

  // Restore the prior query ONLY when arriving via the detail screen's "Back to
  // transaction listing" link, which sets `state.restore` (issue #55). Any
  // other entry (menu, fresh nav, dashboard link) leaves it unset, so the
  // snapshot is discarded and the screen starts clean. Computed once at mount —
  // the snapshot is consumed immediately so a later same-screen reset sticks.
  const restored = useRef<TransactionsSnapshot | null>(
    locationState?.restore ? loadSnapshot(customerNumber) : null,
  );

  // Issue #344: whether this listing was reached from a Security screen's
  // "Check Transactions" action, and where "Back to High Security" should
  // return to. Set either directly by that navigation (`fromSecurity` +
  // `returnTo` in `location.state`), or carried forward through the
  // transaction-detail drill-down/back round trip via the same snapshot the
  // #55 restore mechanism already uses (`restored.current`).
  const securityReturn = useRef<{ returnTo: string } | null>(
    locationState?.fromSecurity && locationState.returnTo
      ? { returnTo: locationState.returnTo }
      : restored.current?.fromSecurity && restored.current.returnTo
        ? { returnTo: restored.current.returnTo }
        : null,
  );
  // Issue #358 ("Option A", @dylan219 2026-07-15): when this listing was
  // reached from a High-Security check's "Check Transactions" action, the
  // customer is only IDENTIFIED (card lookup done, 2FA not yet completed), so
  // the medium-gated servicing reads (`/customers/accounts`,
  // `/accounts/transactions`, `/accounts/transactions/pending`) 403 ("Could not
  // load accounts"). @dylan219 authorised reading accounts + transactions at
  // the identified level in this flow ONLY, so here we call the identified-
  // gated `/security/*` twins instead. Outside the security flow the medium
  // endpoints are unchanged.
  const fromSecurity = !!securityReturn.current;

  // The restore flag is a strict one-shot. `BrowserRouter` persists route state
  // in `window.history.state`, which survives a full reload to the same URL
  // (e.g. a later `goto('/transactions')` or a browser refresh); left in place
  // it would wrongly re-trigger a restore on the next plain entry. So on mount
  // we always strip the flag from the current history entry by replacing the
  // route state — a restoring entry has already read the snapshot into
  // `restored.current`, and a fresh (non-restoring) entry additionally drops
  // the stale snapshot so it can't be reused.
  useEffect(() => {
    if (!restored.current) clearSnapshot();
    if ((location.state as { restore?: boolean } | null)?.restore) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedAccount, setSelectedAccount] = useState(
    restored.current?.selectedAccount ?? '',
  );
  const [option, setOption] = useState(restored.current?.option ?? ''); // '' | 'historic' | 'pending'
  const [fromDate, setFromDate] = useState(restored.current?.fromDate ?? '');
  const [toDate, setToDate] = useState(restored.current?.toDate ?? '');
  const [fromDateError, setFromDateError] = useState('');
  const [toDateError, setToDateError] = useState('');
  const [rangeError, setRangeError] = useState('');
  const [selectedType, setSelectedType] = useState(
    restored.current?.selectedType ?? '',
  );
  const [amountSearch, setAmountSearch] = useState(
    restored.current?.amountSearch ?? '',
  );

  // Results are NOT restored from the snapshot — they are re-fetched through the
  // gated BFF call on restore (see the restore effect below) so the server-side
  // guards are re-enforced and an expired session fails closed (issue #55).
  const [historic, setHistoric] = useState<TxnRow[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [pending, setPending] = useState<PendingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Client-side pagination page indices for the two long lists (#481). Kept
  // independent so paging one list never moves the other; each resets to page 0
  // when its underlying data reloads (see the effects below).
  const [pendingPage, setPendingPage] = useState(0);
  const [historicPage, setHistoricPage] = useState(0);

  // Reset each list to its first page whenever its data reloads (#481) so a new
  // fetch (or a shrunk list) never leaves the view on an out-of-range page.
  useEffect(() => {
    setPendingPage(0);
  }, [pending]);
  useEffect(() => {
    setHistoricPage(0);
  }, [historic]);

  const account = accounts.find((a) => a.AccountNumber === selectedAccount);

  // Load the customer's accounts for the account-selector dropdown (CCF-82).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        // #358: identified-gated twin when reached from a security check.
        const { data, error: apiError } = fromSecurity
          ? await api.POST('/security/accounts', {
              params: { header: { 'X-Teller-Id': tellerId } },
              body: { customerNumber },
            })
          : await api.POST('/customers/accounts', {
              params: { header: { 'X-Teller-Id': tellerId } },
              body: { customerNumber },
            });
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  function resetResults(): void {
    setShowResults(false);
    setHistoric([]);
    setPending(null);
    setFromDate('');
    setToDate('');
    setFromDateError('');
    setToDateError('');
    setRangeError('');
    setSelectedType('');
    setAmountSearch('');
  }

  async function loadHistoric(): Promise<void> {
    const fromErr = validateDate(fromDate);
    const toErr = validateDate(toDate);
    const rangeErr = validateRange(fromDate, toDate);
    setFromDateError(fromErr);
    setToDateError(toErr);
    setRangeError(rangeErr);
    if (!fromDate) {
      setFromDateError('From date is required');
      return;
    }
    if (!toDate) {
      // To date is optional in the API but CCF-47 makes it mandatory on-screen.
      setToDateError('To date is required');
      return;
    }
    if (fromErr || toErr || rangeErr) {
      toast.error('Please fix the date errors before searching.');
      return;
    }
    setLoading(true);
    setError('');
    const fromIso = toIsoDate(fromDate);
    const toIso = toIsoDate(toDate);
    try {
      const body = {
        customerNumber,
        accountNumber: selectedAccount,
        // Send the optional date filters only when `toIsoDate` yields a real
        // YYYY-MM-DD value — never an empty string — so a malformed/incomplete
        // entry can't put a `fromDate: ""` on the wire (consistent with the
        // type/amount filters below). The on-screen guards above already
        // require both dates, so in normal flow both are present.
        ...(fromIso ? { fromDate: fromIso } : {}),
        ...(toIso ? { toDate: toIso } : {}),
        ...(selectedType ? { transactionType: selectedType } : {}),
        ...(amountSearch.trim() ? { amountSearch: amountSearch.trim() } : {}),
      };
      // #358: identified-gated twin when reached from a security check.
      const {
        data,
        error: apiError,
        response,
      } = fromSecurity
        ? await api.POST('/security/transactions', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body,
          })
        : await api.POST('/accounts/transactions', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body,
          });
      if (apiError || !data) {
        setError(
          gatewayErrorMessage(apiError, response.status, 'Search failed'),
        );
        return;
      }
      const rows = (data.Transactions ?? []) as unknown as TxnRow[];
      setHistoric(rows);
      setShowResults(true);
      if (!rows.length) toast.info('No transactions found.');
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  async function loadPending(): Promise<void> {
    setLoading(true);
    setError('');
    try {
      // #358: identified-gated twin when reached from a security check.
      const {
        data,
        error: apiError,
        response,
      } = fromSecurity
        ? await api.POST('/security/transactions/pending', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber, accountNumber: selectedAccount },
          })
        : await api.POST('/accounts/transactions/pending', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber, accountNumber: selectedAccount },
          });
      if (apiError || !data) {
        setError(
          gatewayErrorMessage(
            apiError,
            response.status,
            'Could not load pending transactions',
          ),
        );
        return;
      }
      setPending(data as unknown as PendingResult);
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  // On a restoring entry (issue #55), re-run the gated BFF query for the
  // restored criteria once there is a verified customer/teller in context. The
  // results are fetched fresh through the BFF route guards (Teller-ID, RBAC,
  // verification, account-ownership), so an expired/cleared session fails
  // closed (401/403) instead of surfacing stale cached rows.
  const restoreRan = useRef(false);
  useEffect(() => {
    const snap = restored.current;
    if (!snap || restoreRan.current || !hasContext) return;
    restoreRan.current = true;
    if (snap.option === 'historic') void loadHistoric();
    else if (snap.option === 'pending') void loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContext]);

  function onOptionChange(value: string): void {
    setOption(value);
    resetResults();
    if (value === 'pending') void loadPending();
  }

  function openTransaction(row: TxnRow): void {
    // Snapshot the current query so it can be restored if the agent returns via
    // the detail screen's "Back to transaction listing" link (issue #55). Also
    // carries `fromSecurity`/`returnTo` (issue #344) so the "Back to High
    // Security" button survives that drill-down/back round trip too.
    saveSnapshot({
      customerNumber,
      selectedAccount,
      option,
      fromDate,
      toDate,
      selectedType,
      amountSearch,
      ...(securityReturn.current
        ? { fromSecurity: true, returnTo: securityReturn.current.returnTo }
        : {}),
    });
    // Carry the account the agent drilled down from so the BFF can bind the
    // transaction to an owned account (account-ownership / IDOR protection).
    // ALSO carry the gateway composite key (InputDate/InputTime/InputTime2/
    // SeqNo) the real `transaction/information` op needs to resolve the row —
    // the gateway does NOT accept an opaque id, so omitting it returned a 400
    // and an empty detail screen (issue #125). The mock still resolves by `id`.
    const params = new URLSearchParams();
    params.set('id', row.TransactionId);
    params.set('account', selectedAccount);
    if (row.InputDate) params.set('inputDate', row.InputDate);
    if (row.InputTime) params.set('inputTime', row.InputTime);
    if (row.InputTime2) params.set('inputTime2', row.InputTime2);
    if (row.SequenceNumber) params.set('sequenceNumber', row.SequenceNumber);
    // SEPA indicator (#125): a non-empty `SepaKeys` marks a SEPA/Direct-Debit
    // transaction. Threaded so the detail screen tells the BFF to make the
    // SECOND call (`transaction/information/sepa`) that fetches the Originating/
    // Destination/Settlement boxes — those §5/6/7 sections render ONLY for SEPA.
    if (row.SepaKeys) params.set('sepaKeys', row.SepaKeys);
    // Carry the listing-only DISPLAY fields the real `transaction/information`
    // op does NOT return (Ref / Dr-Cr / Type / Date) so the detail screen can
    // still show them — the gateway projects only the rich Financial/Audit
    // detail, not these row attributes (issue #125). Display-only: never sent to
    // the BFF (the detail call resolves by the composite key above).
    if (row.Ref) params.set('ref', row.Ref);
    if (row.DrCr) params.set('drCr', row.DrCr);
    if (row.Type) params.set('type', row.Type);
    if (row.Date) params.set('date', row.Date);
    // Account currency (issue #415): the `transaction/information` op carries no
    // currency, so thread the drilled-down account's ISO code so the detail
    // screen renders amounts in the right symbol (USD → US$, …), not a fixed €.
    if (account?.Currency) params.set('currency', account.Currency);
    navigate(`/transaction-information?${params.toString()}`);
  }

  const pendingItems = pending?.PendingTransactions ?? [];

  // Client-side pagination of the two long lists (#481). Pages are clamped so a
  // shrunk list can never leave the view on an out-of-range page.
  const pendingPageCount = Math.max(
    1,
    Math.ceil(pendingItems.length / PENDING_TXN_PER_PAGE),
  );
  const pendingCurrentPage = Math.min(pendingPage, pendingPageCount - 1);
  const pagedPending = pendingItems.slice(
    pendingCurrentPage * PENDING_TXN_PER_PAGE,
    pendingCurrentPage * PENDING_TXN_PER_PAGE + PENDING_TXN_PER_PAGE,
  );

  const historicPageCount = Math.max(
    1,
    Math.ceil(historic.length / HISTORIC_TXN_PER_PAGE),
  );
  const historicCurrentPage = Math.min(historicPage, historicPageCount - 1);
  const pagedHistoric = historic.slice(
    historicCurrentPage * HISTORIC_TXN_PER_PAGE,
    historicCurrentPage * HISTORIC_TXN_PER_PAGE + HISTORIC_TXN_PER_PAGE,
  );

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="transactions-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-6 flex-wrap">
          {/* Issue #358 (@dylan219 2026-07-15): when reached from a High-
              Security check (pre-2FA), the ONLY exit is back to the originating
              security screen — the customer is not yet fully verified (medium),
              so the agent must not step off into the dashboard / other servicing
              screens from here. Hidden in that flow; shown normally otherwise. */}
          {!fromSecurity && (
            <a
              href="/dashboard"
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
              className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
            >
              &lt; Back to customer dashboard
            </a>
          )}

          {/* Issue #344 (@dylan219): only shown when this listing was reached
              from a Security screen's "Check Transactions" action (never on
              the normal Dashboard→Accounts→Transactions path). Navigates back
              WITH `restoreSecurityRun`, so the security screen resumes the
              agent's in-progress Pass/Fail / 2FA state where they left off. */}
          {securityReturn.current && (
            <a
              href={securityReturn.current.returnTo}
              onClick={(e) => {
                e.preventDefault();
                navigate(securityReturn.current!.returnTo, {
                  state: { restoreSecurityRun: true },
                });
              }}
              className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
            >
              &lt; Back to High Security
            </a>
          )}
        </div>

        {/* Email-templates menu — #795 (@dylan219): shown on BOTH the Historic
            AND the Pending Transactions (SCRN-48) views, presented the same way
            (the top-right "Email templates" dropdown) as on the Historic view.
            Same template set + wording; permission-free (#710), the generated
            `.eml` filename is keyed on the subject (never the CIF, #738). */}
        {(option === 'historic' || option === 'pending') && (
          <EmailTemplatesMenu
            customerName={customer.name ?? ''}
            accountNumber={selectedAccount}
            content={contentMap}
            tellerId={tellerId}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="transactions-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Transactions
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). Issue #358: suppressed in the High-Security flow
            (pre-2FA) so the agent's only exit is back to the security screen. */}
        {!fromSecurity && (
          <ProcessMenu
            options={PROCESS_MENU_OPTIONS}
            onSelect={onProcessSelect}
          />
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Account + transaction-option selection */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <div className="w-[400px]">
          <Dropdown
            id="account-selection"
            label="Select account"
            placeholder="Select account"
            value={selectedAccount}
            onChange={(val) => {
              setSelectedAccount(val);
              setOption('');
              resetResults();
            }}
            options={accounts.map((a) => ({
              value: a.AccountNumber,
              // Canonical account-dropdown label (#381): "<name> <last4>",
              // matching Transfer Between Accounts (last-4 only per CCF-80).
              label: accountOptionLabel(a),
            }))}
          />
        </div>

        {selectedAccount && (
          <div className="w-[400px]">
            <Dropdown
              id="transaction-option"
              label="Transaction options"
              placeholder="Select transaction option"
              value={option}
              onChange={onOptionChange}
              options={[
                { value: 'historic', label: 'Historic transactions' },
                { value: 'pending', label: 'Pending transactions' },
              ]}
            />
          </div>
        )}
      </div>

      {/* Account owner + current balance (read-only) once an option is chosen. */}
      {selectedAccount && option && account && (
        <div className="mb-8 border-2 border-[#e5e7eb] bg-[#f9fafb] p-6 inline-block">
          <div className="flex gap-x-16 flex-wrap">
            <div className="flex items-center gap-x-3">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Account owner:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {account.Owners
                  ? formatOwners(account.Owners)
                  : (customer.name ?? '—')}
              </span>
            </div>
            <div
              className="w-[1px] h-[30px] bg-[#d1d5db]"
              aria-hidden="true"
            ></div>
            <div className="flex items-center gap-x-3">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Current balance:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {formatCurrency(account.Balance, account.Currency)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Historic transactions search form (CCF-47). */}
      {option === 'historic' && (
        <div className="mb-8">
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-4">
            Note: Each search may cover a period of up to 12 months (for
            example, March to March). From and To dates are mandatory.
          </p>

          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="from-date"
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
              >
                From
              </label>
              <div className="relative w-[280px]">
                <input
                  id="from-date"
                  type="text"
                  value={fromDate}
                  onChange={(e) => {
                    const masked = formatDateInput(e.target.value);
                    setFromDate(masked);
                    setFromDateError(validateDateWhileTyping(masked));
                    setRangeError(validateRange(masked, toDate));
                  }}
                  onBlur={() => setFromDateError(validateDate(fromDate))}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="dd/mm/yyyy"
                  aria-label="From date"
                  aria-invalid={!!fromDateError}
                  className={`w-full pl-4 pr-10 py-3 border-2 bg-white rounded-lg font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-2 focus:ring-[#005fcc]/20 ${
                    fromDateError ? 'border-[#dc2626]' : 'border-[#d1d5db]'
                  }`}
                />
                <DatePicker
                  fieldLabel="From"
                  value={fromDate}
                  onSelect={(picked) => {
                    const masked = formatDateInput(picked);
                    setFromDate(masked);
                    setFromDateError(validateDate(masked));
                    setRangeError(validateRange(masked, toDate));
                  }}
                />
              </div>
              <div className="min-h-[20px]">
                {fromDateError && (
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    role="alert"
                  >
                    {fromDateError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="to-date"
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
              >
                To
              </label>
              <div className="relative w-[280px]">
                <input
                  id="to-date"
                  type="text"
                  value={toDate}
                  onChange={(e) => {
                    const masked = formatDateInput(e.target.value);
                    setToDate(masked);
                    setToDateError(validateDateWhileTyping(masked));
                    setRangeError(validateRange(fromDate, masked));
                  }}
                  onBlur={() => setToDateError(validateDate(toDate))}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="dd/mm/yyyy"
                  aria-label="To date"
                  aria-invalid={!!toDateError}
                  className={`w-full pl-4 pr-10 py-3 border-2 bg-white rounded-lg font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-2 focus:ring-[#005fcc]/20 ${
                    toDateError ? 'border-[#dc2626]' : 'border-[#d1d5db]'
                  }`}
                />
                <DatePicker
                  fieldLabel="To"
                  value={toDate}
                  onSelect={(picked) => {
                    const masked = formatDateInput(picked);
                    setToDate(masked);
                    setToDateError(validateDate(masked));
                    setRangeError(validateRange(fromDate, masked));
                  }}
                />
              </div>
              <div className="min-h-[20px]">
                {toDateError && (
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    role="alert"
                  >
                    {toDateError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="select-type"
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
              >
                Transaction type
              </label>
              <select
                id="select-type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                aria-label="Select transaction type"
                className="w-[280px] px-4 py-3 border-2 border-[#d1d5db] bg-white rounded-lg font-['Arial',sans-serif] text-[16px] text-[#1d252c] focus:outline-none focus:border-[#005fcc] focus:ring-2 focus:ring-[#005fcc]/20 cursor-pointer"
              >
                {TRANSACTION_TYPES.map((t) => (
                  <option key={t.value || 'all'} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <div className="min-h-[20px]" />
            </div>

            {/* Optional amount search (CCF-47), format 1025.99. */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="amount-search"
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
              >
                Amount (optional)
              </label>
              <input
                id="amount-search"
                type="text"
                inputMode="decimal"
                value={amountSearch}
                onChange={(e) => setAmountSearch(e.target.value)}
                placeholder="1025.99"
                aria-label="Amount search"
                className="w-[200px] px-4 py-3 border-2 border-[#d1d5db] bg-white rounded-lg font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-2 focus:ring-[#005fcc]/20"
              />
              <div className="min-h-[20px]" />
            </div>

            <div className="flex flex-col gap-1">
              <div className="h-[20px]" />
              <button
                type="button"
                onClick={() => void loadHistoric()}
                disabled={
                  loading || !!fromDateError || !!toDateError || !!rangeError
                }
                aria-label="Search"
                className="px-5 py-3 bg-[#FF703E] text-[#1d252c] font-['Arial',sans-serif] font-bold text-[16px] rounded-sm hover:bg-[#e55a2a] transition-all cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#FF703E]"
              >
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>

          {rangeError && (
            <p
              className="mt-2 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
              role="alert"
            >
              {rangeError}
            </p>
          )}
        </div>
      )}

      {/* Pending transactions (SCRN 48 / CCF-32). */}
      {option === 'pending' && (
        <section aria-label="Pending transactions" className="mb-6">
          {/* Summary overview (read-only): balances + per-hold-type buckets
              (CCF-32 — POS/VPP/ATM/CHQ/STM/PRT). */}
          <div className="border-2 border-[#dad7d3] bg-white p-8 max-w-[900px]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
              <div>
                <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                  Account Balance
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                  {formatCurrency(account?.Balance, account?.Currency)}
                </p>
              </div>
              <div>
                <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                  Available Balance
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                  {formatCurrency(account?.AvailableBalance, account?.Currency)}
                </p>
              </div>
              {(
                [
                  ['POS Holds', pending?.Summary?.PosHolds, 'pos-holds'],
                  ['VPP Holds', pending?.Summary?.VppHolds, 'vpp-holds'],
                  ['ATM Holds', pending?.Summary?.AtmHolds, 'atm-holds'],
                  ['CHQ Holds', pending?.Summary?.ChqHolds, 'chq-holds'],
                  ['STM Holds', pending?.Summary?.StmHolds, 'stm-holds'],
                  ['PRT Holds', pending?.Summary?.PrtHolds, 'prt-holds'],
                ] as const
              ).map(([label, value, key]) => (
                <div key={key}>
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                    {label}
                  </p>
                  <p
                    className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                    aria-label={label}
                  >
                    {formatCurrency(value ?? 0, account?.Currency)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Pending holds table. */}
          <div className="mt-6 border-2 border-[#dad7d3] bg-white p-8 max-w-[1200px]">
            <div className="overflow-x-auto">
              <table className="w-full" role="table">
                <thead>
                  <tr className="border-b border-[#dad7d3]">
                    {[
                      'Expiry Date',
                      'CHQ/MGT',
                      'Amount',
                      'Reason',
                      'Merchant',
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-4 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : pendingItems.length > 0 ? (
                    pagedPending.map((t) => (
                      // Pending holds are a READ-ONLY overview (SCRN 48 / CCF-32)
                      // — deliberately NOT drill-down candidates. Only the
                      // historic listing (CCF-47) supports the Transaction
                      // Information detail screen (SCRN 47 / CCF-86). A hold row
                      // has no settled composite key (InputDate/InputTime/
                      // InputTime2/SequenceNumber) that the real gateway's
                      // `transaction/information` op requires, so drilling in
                      // produced a "Could not load transaction (400)" dead-end
                      // (#195). Rows are therefore non-interactive.
                      <tr
                        key={t.TransactionId}
                        className="border-b border-[#dad7d3]"
                        aria-label={`Pending transaction ${t.TransactionId}`}
                      >
                        {/* Expiry Date — latest auto-expiry of the hold (CCF-32). */}
                        <td className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {t.ExpiryDate ? formatDate(t.ExpiryDate) : ''}
                        </td>
                        {/* Hold type M/P/A/C (CCF-32 "CHQ/MGT" column). */}
                        <td className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {t.HoldType
                            ? (HOLD_TYPE_LABEL[t.HoldType] ?? t.HoldType)
                            : ''}
                        </td>
                        <td className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {formatCurrency(t.Amount, account?.Currency)}
                        </td>
                        <td className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {t.Description}
                        </td>
                        <td className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {t.Merchant ?? ''}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-3 px-4 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                      >
                        No pending transactions.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pending list pagination (#481 "List lengths"). At most
              PENDING_TXN_PER_PAGE (6) holds are rendered per page; Previous is
              disabled on the first page, Next on the last. Distinct aria-labels
              keep it unambiguous from the historic controls on the same screen. */}
          {pendingItems.length > 0 && (
            <div className="mt-6 flex items-center gap-4">
              <button
                type="button"
                disabled={pendingCurrentPage <= 0}
                onClick={() => setPendingPage((p) => Math.max(0, p - 1))}
                aria-label="Previous pending transactions page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                &lt; Previous
              </button>
              <span
                aria-live="polite"
                className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
              >
                Page {pendingCurrentPage + 1} of {pendingPageCount}
              </span>
              <button
                type="button"
                disabled={pendingCurrentPage >= pendingPageCount - 1}
                onClick={() =>
                  setPendingPage((p) => Math.min(pendingPageCount - 1, p + 1))
                }
                aria-label="Next pending transactions page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                Next &gt;
              </button>
            </div>
          )}
        </section>
      )}

      {/* Historic transactions results table (CCF-47). */}
      {option === 'historic' && showResults && (
        <div className="overflow-x-auto max-w-[1400px]">
          <table className="w-full border-collapse bg-white" role="table">
            <thead>
              <tr>
                {[
                  '#',
                  'Date',
                  'Ref',
                  'Description',
                  'Withdrawal',
                  'Deposit',
                  'Balance',
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historic.length > 0 ? (
                pagedHistoric.map((t, index) => (
                  <tr
                    key={t.TransactionId}
                    className="border-b border-[#e5e7eb] hover:bg-[#f3f4f6] cursor-pointer"
                    tabIndex={0}
                    onClick={() => openTransaction(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openTransaction(t);
                      }
                    }}
                    aria-label={`Transaction ${t.TransactionId}`}
                  >
                    {/* Selection (sequential) column — CCF-47. Numbered
                        continuously across pages (#481). */}
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {historicCurrentPage * HISTORIC_TXN_PER_PAGE + index + 1}
                    </td>
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {formatDate(t.Date)}
                    </td>
                    {/* Ref shown as the 6-digit reference (CCF-47), not the id. */}
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {t.Ref ?? ''}
                    </td>
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {t.Description}
                    </td>
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {t.DrCr === 'DR'
                        ? formatCurrency(t.Amount, account?.Currency)
                        : ''}
                    </td>
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {t.DrCr === 'CR'
                        ? formatCurrency(t.Amount, account?.Currency)
                        : ''}
                    </td>
                    <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                      {formatCurrency(t.RunningBalance, account?.Currency)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                  >
                    No transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Historic list pagination (#481 "List lengths"). At most
              HISTORIC_TXN_PER_PAGE (16) rows per page; drill-down is preserved
              on the paged rows. Distinct aria-labels keep it unambiguous from
              the pending controls on the same screen. */}
          {historic.length > 0 && (
            <div className="mt-6 flex items-center gap-4">
              <button
                type="button"
                disabled={historicCurrentPage <= 0}
                onClick={() => setHistoricPage((p) => Math.max(0, p - 1))}
                aria-label="Previous historic transactions page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                &lt; Previous
              </button>
              <span
                aria-live="polite"
                className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
              >
                Page {historicCurrentPage + 1} of {historicPageCount}
              </span>
              <button
                type="button"
                disabled={historicCurrentPage >= historicPageCount - 1}
                onClick={() =>
                  setHistoricPage((p) => Math.min(historicPageCount - 1, p + 1))
                }
                aria-label="Next historic transactions page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                Next &gt;
              </button>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
